import React, { useMemo } from "react";
import { Check, X } from "lucide-react";

/**
 * Password strength meter:
 * - Computes a 0-4 score from length + character class diversity.
 * - Renders 4 colored bars + a text label.
 * - Lists actionable hints below to guide the user.
 *
 * Returns: <div> meter; the parent decides what to do with `score` if needed.
 */
export function scorePassword(pw) {
  if (!pw) return { score: 0, checks: defaultChecks() };
  const checks = {
    length:  pw.length >= 8,
    upper:   /[A-Z]/.test(pw),
    lower:   /[a-z]/.test(pw),
    digit:   /\d/.test(pw),
    symbol:  /[^A-Za-z0-9]/.test(pw),
  };
  let score = 0;
  if (checks.length) score++;
  // Diversity: how many of upper/lower/digit/symbol are met?
  const diversity = ["upper", "lower", "digit", "symbol"].filter(k => checks[k]).length;
  if (diversity >= 2) score++;
  if (diversity >= 3) score++;
  if (diversity === 4 && pw.length >= 12) score++;
  return { score, checks };
}

function defaultChecks() {
  return { length: false, upper: false, lower: false, digit: false, symbol: false };
}

const LEVELS = [
  { label: "Too short",   color: "#52525B" },
  { label: "Weak",        color: "#FF007F" },
  { label: "Okay",        color: "#FF9B00" },
  { label: "Strong",      color: "#00F0FF" },
  { label: "Very strong", color: "#D4FF00" },
];

export default function PasswordStrengthMeter({ password = "", className = "" }) {
  const { score, checks } = useMemo(() => scorePassword(password), [password]);
  const lvl = LEVELS[score];

  return (
    <div className={className} data-testid="password-strength-meter">
      <div className="flex gap-1 mt-1.5" aria-label={`Password strength: ${lvl.label}`}>
        {[0, 1, 2, 3].map(i => (
          <div key={i}
            className="flex-1 h-1 rounded-full transition-colors"
            style={{
              backgroundColor: i < score ? lvl.color : "#3D2845",
            }}/>
        ))}
      </div>
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-[10px] uppercase tracking-widest font-bold"
          style={{ color: lvl.color }}
          data-testid="password-strength-label">
          {lvl.label}
        </span>
        {password && score < 3 && (
          <span className="text-[10px] text-[#A1A1AA]">
            Add {[
              !checks.length && "8+ chars",
              !checks.upper  && "an UPPERCASE",
              !checks.digit  && "a number",
              !checks.symbol && "a symbol",
            ].filter(Boolean).slice(0, 2).join(" + ")}
          </span>
        )}
      </div>
      {password && (
        <ul className="grid grid-cols-2 gap-x-3 gap-y-0.5 mt-2" data-testid="password-checks">
          <Hint ok={checks.length}  label="At least 8 characters"/>
          <Hint ok={checks.upper}   label="Uppercase letter"/>
          <Hint ok={checks.lower}   label="Lowercase letter"/>
          <Hint ok={checks.digit}   label="Number"/>
          <Hint ok={checks.symbol}  label="Symbol (!@#…)"/>
        </ul>
      )}
    </div>
  );
}

function Hint({ ok, label }) {
  return (
    <li className={`flex items-center gap-1 text-[10px] ${ok ? "text-[#D4FF00]" : "text-[#52525B]"}`}>
      {ok ? <Check size={10}/> : <X size={10}/>}
      <span>{label}</span>
    </li>
  );
}
